export interface HistoryMonth {
  month: string;
  income: number | null;
  mileage: number | null;
  operatingCosts: number | null;
  repairCosts: number | null;
  yardRepairCosts: number | null;
  missingGrossEntries: number;
  missingMileageEntries: number;
  unpricedRepairs: number;
}

export interface ProfitabilityHistory {
  asOf: string;
  currentMileage: number | null;
  monthlyMileageFromMaintenance: number | null;
  monthlyLoanPayment: number | null;
  estimatedLoanBalance: number | null;
  loanAnnualInterestPercent: number | null;
  askingPrice: number | null;
  months: HistoryMonth[];
  maintenance: {
    operation: string;
    dueDate: string | null;
    dueMileage: number | null;
  }[];
  warnings: string[];
}

export interface Assumptions {
  income: number | null;
  miles: number | null;
  operatingPerMile: number | null;
  repairsPerMile: number | null;
  maintenancePerMile: number | null;
  ownership: number | null;
  loanPayment: number | null;
  repairGrowth: number | null;
  resale: number | null;
  resaleDecline: number | null;
  loanBalance: number | null;
  loanInterest: number | null;
}

export const requiredFields: (keyof Assumptions)[] = [
  "income",
  "miles",
  "operatingPerMile",
  "repairsPerMile",
  "maintenancePerMile",
  "ownership",
  "loanPayment",
  "repairGrowth",
];
export const round = (n: number) =>
  Math.round((n + Number.EPSILON) * 100) / 100;
const valid = (n: number | null): n is number =>
  n !== null && Number.isFinite(n) && n >= 0 && n <= 1e9;
const mean = (values: number[]) =>
  values.length >= 3
    ? round(values.reduce((a, b) => a + b, 0) / values.length)
    : null;
export const recordedRepairCosts = (m: HistoryMonth) => {
  const costs = [m.repairCosts, m.yardRepairCosts].filter(
    (n): n is number => n !== null,
  );
  return costs.length ? round(costs.reduce((a, b) => a + b, 0)) : null;
};
export const recordedCosts = (m: HistoryMonth) => {
  const costs = [m.operatingCosts, recordedRepairCosts(m)].filter(
    (n): n is number => n !== null,
  );
  return costs.length ? round(costs.reduce((a, b) => a + b, 0)) : null;
};

export function defaultAssumptions(history: ProfitabilityHistory): Assumptions {
  const priced = history.months.filter(
    (m) =>
      recordedRepairCosts(m) !== null &&
      m.mileage !== null &&
      m.mileage > 0 &&
      !m.missingMileageEntries &&
      !m.unpricedRepairs,
  );
  return {
    income: mean(
      history.months
        .filter((m) => !m.missingGrossEntries && m.income !== null)
        .map((m) => m.income!),
    ),
    miles:
      mean(
        history.months
          .filter((m) => !m.missingMileageEntries && m.mileage !== null)
          .map((m) => m.mileage!),
      ) ?? history.monthlyMileageFromMaintenance,
    operatingPerMile: null,
    // Use only months containing observed repair costs and mileage; absent invoices are not zero.
    repairsPerMile:
      priced.length >= 3
        ? Math.round(
            (priced.reduce((s, m) => s + recordedRepairCosts(m)!, 0) /
              priced.reduce((s, m) => s + m.mileage!, 0)) *
              10000,
          ) / 10000
        : null,
    maintenancePerMile: null,
    ownership: null,
    loanPayment: history.monthlyLoanPayment,
    repairGrowth: 0,
    resale: history.askingPrice,
    resaleDecline: null,
    loanBalance: history.estimatedLoanBalance,
    loanInterest: history.loanAnnualInterestPercent,
  };
}

export function monthKey(asOf: string, offset: number) {
  const date = new Date(asOf);
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + offset, 1),
  ).toISOString();
}
export const monthLabel = (date: string) =>
  new Date(date).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
export const money = (n: number | null) =>
  n === null
    ? "Unknown"
    : n.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
      });

export interface ForecastMonth {
  month: string;
  miles: number | null;
  income: number | null;
  operating: number | null;
  repairs: number | null;
  maintenance: number | null;
  ownership: number | null;
  loanPayment: number | null;
  expenses: number | null;
  net: number | null;
  cumulative: number | null;
  resale: number | null;
  loanBalance: number | null;
  exitValue: number | null;
  maintenanceDue: string[];
  consistentlyUnprofitable: boolean;
}

export function calculateForecast(
  history: ProfitabilityHistory,
  a: Assumptions,
) {
  const missing = requiredFields.filter((key) => !valid(a[key]));
  if (a.repairGrowth !== null && a.repairGrowth > 100)
    missing.push("repairGrowth");
  const complete = !missing.length;
  const loanProjection =
    complete &&
    valid(a.loanBalance) &&
    valid(a.loanInterest) &&
    a.loanInterest <= 100;
  const economic =
    loanProjection &&
    valid(a.resale) &&
    valid(a.resaleDecline) &&
    a.resaleDecline <= 100;
  let cumulative = 0;
  let balance = loanProjection ? a.loanBalance! : null;
  const rows: ForecastMonth[] = [];
  const scheduledMaintenance = new Set<number>();
  for (let i = 0; i < 12; i++) {
    const month = monthKey(history.asOf, i + 1);
    const operating =
      valid(a.miles) && valid(a.operatingPerMile)
        ? round(a.miles * a.operatingPerMile)
        : null;
    const repairs =
      valid(a.miles) &&
      valid(a.repairsPerMile) &&
      valid(a.repairGrowth) &&
      a.repairGrowth <= 100
        ? round(
            a.miles * a.repairsPerMile * Math.pow(1 + a.repairGrowth / 100, i),
          )
        : null;
    const maintenance =
      valid(a.miles) && valid(a.maintenancePerMile)
        ? round(a.miles * a.maintenancePerMile)
        : null;
    let loanPayment = valid(a.loanPayment) ? a.loanPayment : null;
    // When payoff information is supplied, stop debt service after payoff and include interest.
    if (balance !== null) {
      const due = round(balance * (1 + a.loanInterest! / 1200));
      loanPayment = Math.min(a.loanPayment!, due);
      balance = round(Math.max(0, due - loanPayment));
    }
    const expenses = complete
      ? round(
          operating! + repairs! + maintenance! + a.ownership! + loanPayment!,
        )
      : null;
    const net = complete ? round(a.income! - expenses!) : null;
    if (net !== null) cumulative = round(cumulative + net);
    const resale = economic
      ? round(a.resale! * Math.pow(1 - a.resaleDecline! / 100, i + 1))
      : null;
    const previousOdometer =
      valid(history.currentMileage) && valid(a.miles)
        ? history.currentMileage + a.miles * i
        : null;
    const odometer =
      previousOdometer !== null ? previousOdometer + a.miles! : null;
    const maintenanceDue = history.maintenance
      .filter((m, index) => {
        if (scheduledMaintenance.has(index)) return false;
        const dueByDate =
          m.dueDate !== null &&
          new Date(m.dueDate).getTime() <
            new Date(monthKey(history.asOf, i + 2)).getTime() &&
          (i === 0 ||
            new Date(m.dueDate).getTime() >= new Date(month).getTime());
        const dueByMiles =
          m.dueMileage !== null &&
          odometer !== null &&
          m.dueMileage <= odometer &&
          (i === 0 || m.dueMileage > previousOdometer!);
        if (dueByDate || dueByMiles) {
          scheduledMaintenance.add(index);
          return true;
        }
        return false;
      })
      .map((m) => m.operation);
    rows.push({
      month,
      miles: valid(a.miles) ? a.miles : null,
      income: valid(a.income) ? a.income : null,
      operating,
      repairs,
      maintenance,
      ownership: valid(a.ownership) ? a.ownership : null,
      loanPayment,
      expenses,
      net,
      cumulative: complete ? cumulative : null,
      resale,
      loanBalance: balance,
      exitValue:
        resale !== null && balance !== null
          ? round(cumulative + resale - balance)
          : null,
      maintenanceDue,
      consistentlyUnprofitable: false,
    });
  }
  let lossStart = -1;
  for (let i = 0; i <= 9; i++) {
    if (rows.slice(i, i + 3).every((r) => r.net !== null && r.net < 0)) {
      if (lossStart < 0) lossStart = i;
      rows.slice(i, i + 3).forEach((r) => {
        r.consistentlyUnprofitable = true;
      });
    }
  }
  let sellAfter: number | null = null;
  let explanation: string;
  if (!complete) {
    explanation =
      "Enter all required assumptions to calculate profit and a selling period. Unknown costs have not been treated as zero.";
  } else if (economic) {
    const sellNow = round(a.resale! - a.loanBalance!);
    let best = sellNow;
    sellAfter = 0;
    rows.forEach((r, i) => {
      if (r.exitValue! >= best) {
        best = r.exitValue!;
        sellAfter = i + 1;
      }
    });
    explanation = `The highest modeled proceeds are ${money(best)} ${sellAfter === 0 ? "by selling now" : `after ${monthLabel(rows[sellAfter! - 1].month)}`}, combining forecast profit, depreciating resale value and estimated loan payoff. Selling now yields ${money(sellNow)}; holding for 12 months yields ${money(rows[11].exitValue)}. Sale fees and taxes are excluded.`;
    if (sellAfter === 12) sellAfter = null;
  } else if (lossStart >= 0) {
    sellAfter = lossStart;
    explanation = `Net profit is projected to stay below zero for at least three consecutive months starting ${monthLabel(rows[lossStart].month)} (${money(rows[lossStart].net)} in the first month). Consider selling before this run of losses begins. Resale and payoff inputs are incomplete; this is based on operating profitability only.`;
  } else {
    explanation = `No sale is recommended within the 12-month forecast period: no run of three consecutive loss-making months is projected. Estimated cumulative profit is ${money(rows[11].cumulative)}. Resale and payoff inputs are incomplete; this is based on operating profitability only.`;
  }
  const limited =
    history.months.filter(
      (m) =>
        m.income !== null &&
        !m.missingGrossEntries &&
        m.mileage !== null &&
        !m.missingMileageEntries,
    ).length < 3;
  return {
    rows,
    missing,
    complete,
    economic,
    lossStart,
    sellAfter,
    explanation,
    limited,
  };
}
